import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  getDispatches,
  getDispatch,
  createDispatch,
  updateDispatch,
  updateDispatchStatus,
  updateDispatchMethod,
  updateDispatchRoute,
  addDispatchGuide,
  approveDispatch,
  getDispatchHistory,
  deleteDispatch,
} from "../../../services";
import type {
  DispatchListParams,
  CreateDispatchPayload,
  UpdateDispatchPayload,
  DispatchHistoryParams,
  DispatchMethod,
  DispatchStatus,
} from "../../../privatePages/Dispatches/types";
import { QUERY_KEYS } from "../queryKeys";

/**
 * Fetches paginated dispatches for the authenticated supplier.
 * GET /v2/dispatches
 */
export const useGetDispatches = (params?: DispatchListParams) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_DISPATCHES, params ?? null],
    queryFn: () => getDispatches(params),
    refetchOnWindowFocus: false,
  });
};

/**
 * Fetches a single dispatch detail with items and history.
 * GET /v2/dispatches/:id
 */
export const useGetDispatch = (id: number) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_DISPATCH, id],
    queryFn: () => getDispatch(id),
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

/**
 * Mutation to create a new pending dispatch.
 * POST /v2/dispatches
 * Invalidates GET_DISPATCHES on success.
 */
export const useCreateDispatch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateDispatchPayload) => createDispatch(payload),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_DISPATCHES]);
    },
  });
};

/**
 * Mutation to update dispatch header fields.
 * PUT /v2/dispatches/:id
 * Invalidates GET_DISPATCHES and GET_DISPATCH on success.
 */
export const useUpdateDispatch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdateDispatchPayload }) =>
      updateDispatch(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_DISPATCHES]);
      queryClient.invalidateQueries([QUERY_KEYS.GET_DISPATCH]);
    },
  });
};

/**
 * Mutation for the status endpoint (kept for the backend PATCH route).
 * PATCH /v2/dispatches/:id/status
 * The UI no longer calls this hook: status changes go through the edit form
 * (PUT with `dispatches:change_status`). The backend endpoint still exists.
 * Invalidates GET_DISPATCHES and GET_DISPATCH on success.
 */
export const useUpdateDispatchStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: DispatchStatus }) =>
      updateDispatchStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_DISPATCHES]);
      queryClient.invalidateQueries([QUERY_KEYS.GET_DISPATCH]);
    },
  });
};

/**
 * Mutation to update the delivery method.
 * PATCH /v2/dispatches/:id/method
 */
export const useUpdateDispatchMethod = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, method }: { id: number; method: DispatchMethod }) =>
      updateDispatchMethod(id, method),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_DISPATCHES]);
      queryClient.invalidateQueries([QUERY_KEYS.GET_DISPATCH]);
    },
  });
};

/**
 * Mutation to update the route.
 * PATCH /v2/dispatches/:id/route
 */
export const useUpdateDispatchRoute = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, route }: { id: number; route: string }) =>
      updateDispatchRoute(id, route),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_DISPATCHES]);
      queryClient.invalidateQueries([QUERY_KEYS.GET_DISPATCH]);
    },
  });
};

/**
 * Mutation to add the tracking guide.
 * POST /v2/dispatches/:id/guide
 */
export const useAddDispatchGuide = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      tracking_guide,
    }: {
      id: number;
      tracking_guide: string;
    }) => addDispatchGuide(id, tracking_guide),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_DISPATCHES]);
      queryClient.invalidateQueries([QUERY_KEYS.GET_DISPATCH]);
    },
  });
};

/**
 * Mutation to approve a pending dispatch.
 * POST /v2/dispatches/:id/approve
 */
export const useApproveDispatch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => approveDispatch(id),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_DISPATCHES]);
      queryClient.invalidateQueries([QUERY_KEYS.GET_DISPATCH]);
    },
  });
};

/**
 * Fetches paginated dispatch history.
 * GET /v2/dispatches/history
 */
export const useGetDispatchHistory = (params?: DispatchHistoryParams) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_DISPATCH_HISTORY, params ?? null],
    queryFn: () => getDispatchHistory(params),
    refetchOnWindowFocus: false,
  });
};

/**
 * Mutation to soft-delete a dispatch.
 * DELETE /v2/dispatches/:id
 */
export const useDeleteDispatch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteDispatch(id),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_DISPATCHES]);
    },
  });
};
